import { Box } from '@mui/material';
import { FsButton } from '../../../../../shared/components/button';
import { LabelBadge } from '../../../../../shared/components/common/labelBadge';
import { Toaster } from '../../../../../shared/components/common/toaster';
import { currencyFormatterHelper } from '../../../../../helpers/currencyFormatterHelper';
import { CURRENCY } from '../../../../../config/appConfig';
import { InvestorDashboardLoanStatuses } from '../../../../../constants/investorDashboardStatus';

const statusColorMap = {
	OnTime: { bg: '#ffffff', text: '#063236', outlineClass: 'outline outline-blue-300', prefix: 'Active' },
	Late: { bg: '#EC9697', text: '#063236', prefix: 'Active' },
	Default: { bg: '#EC9697', text: '#063236' },
	Completed: { bg: '#00D175', text: '#063236' },
	FullyFilled: { bg: '#00D175', text: '#063236' },
	Approved: { bg: '#00D175', text: '#063236' },
};

const statusLabel = (raw) => {
	const friendly = InvestorDashboardLoanStatuses.find((status) => status.id === raw)?.name ?? raw;
	const colors = statusColorMap[raw];
	return colors?.prefix ? `${colors.prefix} · ${friendly}` : friendly;
};

export const InitializeInvestorLoansColumns = (t, onDisburse, onViewAgreement) => {
	return [
		{
			header: t('businessName'),
			accessorKey: 'businessName',
			size: 220,
		},
		{
			header: t('loanId'),
			accessorKey: 'loanId',
			size: 150,
			enableClickToCopy: true,
			muiTableBodyCellCopyButtonProps: ({ cell }) => {
				const loanId = (cell?.getValue() || '').toString().toUpperCase();
				return {
					title: loanId,
					onClick: () => {
						navigator.clipboard.writeText(loanId);
						Toaster({ msg: t('copied'), isError: false });
					},
				};
			},
			Cell: ({ cell }) => (cell?.getValue() || '').toString().toUpperCase().split('-')[0],
		},
		{
			header: t('loanAmount'),
			accessorKey: 'loanAmount',
			Cell: ({ cell }) => `${CURRENCY} ${currencyFormatterHelper(cell?.getValue())}`,
			size: 100,
		},
		{
			header: t('disbursedAmount'),
			accessorKey: 'disbursedAmount',
			Cell: ({ cell }) => `${CURRENCY} ${currencyFormatterHelper(cell?.getValue())}`,
			size: 120,
		},
		{
			header: t('repaid'),
			accessorKey: 'repaymentPercentage',
			Cell: ({ cell, row }) => {
				const pct = Math.min(100, Math.max(0, cell?.getValue() || 0));
				return (
					<Box className="flex flex-col gap-1 min-w-[100px]">
						<span className="text-xs font-bold">{pct}%</span>
						<Box className="w-full h-1.5 rounded-full bg-investor-mint/30 overflow-hidden">
							<Box
								className="h-full bg-investor-green rounded-full"
								sx={{ width: `${pct}%` }}
							/>
						</Box>
					</Box>
				);
			},
			size: 160,
		},
		{
			header: t('status'),
			accessorKey: 'status',
			Cell: ({ cell }) => {
				const raw = cell?.getValue();
				const colors = statusColorMap[raw] ?? { bg: 'rgba(150,150,150,0.15)', text: '#6e8192' };
				return (
					<LabelBadge
						label={statusLabel(raw)}
						bgColor={colors.bg}
						textColor={colors.text}
						classList={`!inline-flex !w-[120px] !rounded-full !px-2 !py-1 !text-[11px] !font-bold !normal-case !whitespace-nowrap ${colors.outlineClass ?? ''}`}
					/>
				);
			},
			size: 125,
		},
		{
			header: t('remaining'),
			accessorKey: 'remainingAmount',
			Cell: ({ row }) => {
				const remaining = (row.original.loanAmount || 0) - (row.original.repaidAmount || 0);
				return `${CURRENCY} ${currencyFormatterHelper(Math.max(0, remaining))}`;
			},
			size: 150,
		},
		{
			id: 'disburse',
			header: t('disburse'),
			size: 130,
			Cell: ({ row }) =>
				row.original.status === 'FullyFilled' ? (
					<FsButton size="sm" onClick={() => onDisburse(row.original)}>
						{t('disburse')}
					</FsButton>
				) : null,
		},
		{
			id: 'agreement',
			header: t('agreement'),
			size: 150,
			Cell: ({ row }) =>
				row.original.agreementAvailable ? (
					<FsButton size="sm" onClick={() => onViewAgreement(row.original)}>
						{t('viewAgreement')}
					</FsButton>
				) : null,
		},
	];
};
